import { Location } from '../types';
import { initialLocations } from '../data/initialLocations';
import { supabase } from './supabase';

const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/locations`;
const STORAGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public`;
const TIMEOUT_MS = 10000;
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
const BUCKET_NAME = import.meta.env.VITE_STORAGE_BUCKET || 'location-images';
const LOCATIONS_PER_PAGE = 21;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries = MAX_RETRIES,
  delay = INITIAL_RETRY_DELAY
): Promise<Response> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Prefer': 'return=representation'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        `HTTP error! status: ${response.status}${
          errorData ? `, message: ${JSON.stringify(errorData)}` : ''
        }`
      );
    }

    return response;
  } catch (error) {
    if (retries === 0) throw error;

    console.warn(`Retry attempt ${MAX_RETRIES - retries + 1} of ${MAX_RETRIES}. Waiting ${delay}ms...`);
    await wait(delay);

    return fetchWithRetry(url, options, retries - 1, delay * 2);
  }
};

export const getLocations = async (
  isAdmin: boolean = false,
  page: number = 1
): Promise<{ locations: Location[]; total: number }> => {
  try {
    const offset = (page - 1) * LOCATIONS_PER_PAGE;
    const query = isAdmin 
      ? `${BASE_URL}?select=*&limit=${LOCATIONS_PER_PAGE}&offset=${offset}`
      : `${BASE_URL}?select=*&status=eq.approved&limit=${LOCATIONS_PER_PAGE}&offset=${offset}`;

    const countQuery = isAdmin
      ? `${BASE_URL}?select=count`
      : `${BASE_URL}?select=count&status=eq.approved`;

    const [response, countResponse] = await Promise.all([
      fetchWithRetry(query, { method: 'GET' }),
      fetchWithRetry(countQuery, { method: 'GET' })
    ]);

    const data = await response.json();
    const countData = await countResponse.json();

    if (!Array.isArray(data)) {
      throw new Error('Invalid data format received from server');
    }

    const locations = data.map((location: any) => ({
      id: location.id,
      name: location.name,
      description: location.description,
      shortDescription: location.short_description,
      category: location.category,
      coordinates: location.coordinates,
      images: (location.images || []).map((image: string) => 
        image.startsWith('http') ? image : `${STORAGE_URL}/${BUCKET_NAME}/${image}`
      ),
      tags: location.tags || [],
      status: location.status,
      created_at: location.created_at,
      updated_at: location.updated_at,
      is_verified: location.is_verified,
      search_vector: location.search_vector,
      short_id: location.short_id
    }));

    const total = parseInt(countData[0]?.count || '0', 10);

    return { locations, total };
  } catch (error) {
    console.error('Error fetching locations:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      error
    });

    return { locations: initialLocations, total: initialLocations.length };
  }
};

export const getLocationByShortId = async (shortId: string): Promise<Location | null> => {
  try {
    const response = await fetchWithRetry(`${BASE_URL}?short_id=eq.${shortId}&select=*`, {
      method: 'GET'
    });

    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }

    const location = data[0];
    return {
      id: location.id,
      name: location.name,
      description: location.description,
      shortDescription: location.short_description,
      category: location.category,
      coordinates: location.coordinates,
      images: (location.images || []).map((image: string) => 
        image.startsWith('http') ? image : `${STORAGE_URL}/${BUCKET_NAME}/${image}`
      ),
      tags: location.tags || [],
      status: location.status,
      created_at: location.created_at,
      updated_at: location.updated_at,
      is_verified: location.is_verified,
      search_vector: location.search_vector,
      short_id: location.short_id
    };
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

export const addLocation = async (
  location: Omit<Location, 'id'>
): Promise<{ data: Location | null; error: string | null }> => {
  try {
    const response = await fetchWithRetry(BASE_URL, {
      method: 'POST',
      body: JSON.stringify({
        name: location.name,
        description: location.description,
        short_description: location.shortDescription,
        category: location.category,
        coordinates: location.coordinates,
        images: location.images,
        tags: location.tags || [],
        status: 'pending', // Always set initial status as pending
        is_verified: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
    });

    const data = await response.json();

    if (!Array.isArray(data) || !data[0]) {
      throw new Error('Unexpected response format from server');
    }

    return { 
      data: {
        id: data[0].id,
        name: data[0].name,
        description: data[0].description,
        shortDescription: data[0].short_description,
        category: data[0].category,
        coordinates: data[0].coordinates,
        images: data[0].images || [],
        tags: data[0].tags || [],
        status: data[0].status,
        created_at: data[0].created_at,
        updated_at: data[0].updated_at,
        is_verified: data[0].is_verified,
        search_vector: data[0].search_vector,
        short_id: data[0].short_id
      }, 
      error: null 
    };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

export const updateLocation = async (
  location: Location
): Promise<{ data: Location | null; error: string | null }> => {
  try {
    const response = await fetchWithRetry(`${BASE_URL}?id=eq.${location.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: location.name,
        description: location.description,
        short_description: location.shortDescription,
        category: location.category,
        coordinates: location.coordinates,
        images: location.images,
        tags: location.tags || [],
        status: location.status,
        is_verified: location.is_verified,
        updated_at: new Date().toISOString()
      })
    });

    const data = await response.json();

    if (!Array.isArray(data) || !data[0]) {
      throw new Error('Unexpected response format from server');
    }

    return { 
      data: {
        id: data[0].id,
        name: data[0].name,
        description: data[0].description,
        shortDescription: data[0].short_description,
        category: data[0].category,
        coordinates: data[0].coordinates,
        images: data[0].images || [],
        tags: data[0].tags || [],
        status: data[0].status,
        created_at: data[0].created_at,
        updated_at: data[0].updated_at,
        is_verified: data[0].is_verified,
        search_vector: data[0].search_vector,
        short_id: data[0].short_id
      }, 
      error: null 
    };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

const deleteImagesFromStorage = async (images: string[]): Promise<void> => {
  const imagesToDelete = images
    .filter(image => !image.startsWith('http'))
    .map(image => image.split('/').pop() || '');

  if (imagesToDelete.length === 0) return;

  const { error } = await supabase
    .storage
    .from(BUCKET_NAME)
    .remove(imagesToDelete);

  if (error) {
    console.error('Error deleting images from storage:', error);
    throw error;
  }
};

export const deleteLocation = async (
  id: string
): Promise<{ error: string | null }> => {
  try {
    // First, get the location to retrieve its images
    const response = await fetchWithRetry(`${BASE_URL}?id=eq.${id}&select=*`, {
      method: 'GET'
    });

    const data = await response.json();
    if (!Array.isArray(data) || !data[0]) {
      throw new Error('Location not found');
    }

    const location = data[0];
    const images = location.images || [];

    // Delete the images from storage first
    await deleteImagesFromStorage(images);

    // Then delete the location record
    await fetchWithRetry(`${BASE_URL}?id=eq.${id}`, {
      method: 'DELETE'
    });

    return { error: null };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};